import React, { useId } from "react";
import Button from "./Button";
import Icon from "./Icon";
import Skeleton from "./Skeleton";
import "./StatePanel.scss";

// StatePanel — Fontaine M37 (`ft-state`). The ONE application-state surface: the six things a
// page can show instead of its content. Visual contract: Style Guide V2 `empty-first-run`,
// `empty-no-results`, `empty-error` and `skeleton-card` (the "never a blank screen" rule).
//
// It owns no routing and no product knowledge: every action is caller-supplied, so the same
// component serves Core List, listings, orders and settings without a single hard-coded path.

/**
 * The six states, each with default copy, medallion icon and tone. Callers override any of it.
 *  - empty        first run — nothing exists yet, invite the first action
 *  - no-results   a filter or query matched nothing — invite a wider net
 *  - loading      data in flight — the shape of what's coming, announced politely
 *  - error        recoverable failure — alert semantics + a real Retry button
 *  - permission   the user's role can't see this — no upsell, no feature name
 *  - locked       the store's plan doesn't include this — upgrade invitation
 */
const STATES = {
  empty: { icon: "Inbox", tone: "accent", title: "Nothing here yet", description: "Add the first one and it'll show up here." },
  "no-results": { icon: "Search", tone: "accent", title: "No matches", description: "Try a broader search, or clear the filters to see everything." },
  loading: { icon: null, tone: "neutral", title: "Loading…", description: null },
  error: { icon: "AlertTriangle", tone: "danger", title: "Something went wrong", description: "Your work is safe — try again." },
  permission: { icon: "Lock", tone: "neutral", title: "You don't have access to this", description: "Your role doesn't include this area. A store owner or admin can open it for you." },
  locked: { icon: "Sparkles", tone: "warning", title: "Not in your plan", description: "Your store's current plan doesn't include this. Your store owner can upgrade, or contact Retail Circle to add it." },
};

const STATE_NAMES = Object.keys(STATES);
const HEADING_LEVELS = [2, 3, 4];

// `action` accepts either a ready-made node or the plain descriptor below, so call sites stay
// declarative. `to` / `href` are honoured by Button itself — StatePanel never builds a path.
function renderAction(action, fallbackVariant, key) {
  if (!action) return null;
  if (React.isValidElement(action)) return React.cloneElement(action, { key });
  const { label, variant = fallbackVariant, size = "md", ...rest } = action;
  if (!label) return null;
  return (
    <Button key={key} variant={variant} size={size} {...rest}>
      {label}
    </Button>
  );
}

/**
 * @param {"empty"|"no-results"|"loading"|"error"|"permission"|"locked"} [state="empty"]
 * @param {string} [title] / [description]      override the state's default copy (null hides it)
 * @param {string|false|ReactNode} [icon]        Icon name, a node, or false for no medallion
 * @param {object|ReactNode} [action]            primary: { label, onClick | to | href, … }
 * @param {object|ReactNode} [secondaryAction]   secondary: same shape
 * @param {Function} [onRetry]                   shorthand for the error state's Retry action
 * @param {"text"|"card"|"row"|"block"} [loadingShape="text"]  skeleton composition while loading
 * @param {number} [loadingCount=1]              how many skeleton specimens to repeat
 * @param {2|3|4} [headingLevel=3]               heading rank for the page it sits in
 * @param {boolean} [compact]                    tighter padding for in-table / in-card slots
 * @param {ReactNode} [children]                 extra content under the actions
 */
export default function StatePanel({
  state = "empty",
  title,
  description,
  icon,
  action,
  secondaryAction,
  onRetry,
  loadingShape = "text",
  loadingCount = 1,
  headingLevel = 3,
  compact = false,
  className = "",
  children,
  ...rest
}) {
  const uid = useId().replace(/:/g, "");
  const resolvedState = STATE_NAMES.includes(state) ? state : "empty";
  const preset = STATES[resolvedState];
  const isLoading = resolvedState === "loading";
  const isError = resolvedState === "error";

  const resolvedTitle = title === undefined ? preset.title : title;
  const resolvedDescription = description === undefined ? preset.description : description;
  const resolvedIcon = icon === undefined ? preset.icon : icon;

  const level = HEADING_LEVELS.includes(headingLevel) ? headingLevel : 3;
  const Heading = `h${level}`;
  const titleId = `${uid}-title`;
  const descId = `${uid}-desc`;

  // Retry is a plain <Button> — a real <button>, so Enter / Space work without extra key handling.
  const primary = action || (isError && onRetry ? { label: "Retry", onClick: onRetry, leadingIcon: <Icon name="RefreshCw" size="sm" /> } : null);
  const actions = [
    renderAction(primary, "primary", "primary"),
    renderAction(secondaryAction, "secondary", "secondary"),
  ].filter(Boolean);

  const classes = [
    "ft-state",
    `ft-state--${resolvedState}`,
    `ft-state--tone-${preset.tone}`,
    compact ? "ft-state--compact" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const medallion =
    resolvedIcon && !isLoading ? (
      <span className="ft-state__medallion" aria-hidden="true">
        {typeof resolvedIcon === "string" ? <Icon name={resolvedIcon} size="lg" /> : resolvedIcon}
      </span>
    ) : null;

  return (
    <div
      className={classes}
      // The error state is the only one that interrupts: alert. Loading announces politely and
      // marks itself busy. The rest are ordinary regions labelled by their own heading.
      role={isError ? "alert" : isLoading ? "status" : resolvedTitle ? "region" : undefined}
      aria-busy={isLoading ? "true" : undefined}
      aria-live={isLoading ? "polite" : undefined}
      aria-labelledby={resolvedTitle ? titleId : undefined}
      aria-describedby={resolvedDescription ? descId : undefined}
      data-state={resolvedState}
      {...rest}
    >
      {medallion}

      {resolvedTitle ? (
        <Heading className="ft-state__title" id={titleId}>
          {resolvedTitle}
        </Heading>
      ) : null}

      {resolvedDescription ? (
        <p className="ft-state__desc" id={descId}>
          {resolvedDescription}
        </p>
      ) : null}

      {isLoading ? (
        // One announcer only: the panel above carries role=status / aria-busy, so the skeleton
        // itself is decorative here (Skeleton's own role/aria are overridden by these props).
        <div className="ft-state__skeleton">
          <Skeleton shape={loadingShape} count={loadingCount} role="presentation" aria-hidden="true" aria-label={undefined} aria-busy={undefined} />
        </div>
      ) : null}

      {actions.length ? <div className="ft-state__actions">{actions}</div> : null}

      {children ? <div className="ft-state__extra">{children}</div> : null}
    </div>
  );
}

export { STATES as STATE_PANEL_STATES };
